/**
 * A customer's relationship stage.
 *
 * The name is always rendered — PRD §9.7 does not permit status by colour
 * alone, and six dots on one hue ramp are not distinguishable to many readers
 * and identical in greyscale.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.3, PRD §5.5, §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { RELATIONSHIP_STAGE_DOT, type RelationshipStage } from './relationship-stages';

export interface RelationshipStageBadgeProps {
  stage: RelationshipStage;
  className?: string;
}

export function RelationshipStageBadge({
  stage,
  className,
}: RelationshipStageBadgeProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'bg-gray-100 text-navy-text inline-flex items-center gap-[6px] rounded-full',
        'font-display px-[11px] py-1 text-[10px] leading-[1.5] font-extrabold',
        'tracking-[0.1em] whitespace-nowrap uppercase',
        className,
      )}
    >
      {/* Decorative: the label beside it already names the stage. */}
      <span
        aria-hidden="true"
        className={cn('size-[7px] rounded-full', RELATIONSHIP_STAGE_DOT[stage])}
      />
      {t(`customer.stage.${stage}`)}
    </span>
  );
}
